import React, { useMemo } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { shareInstagram } from 'src/app/shared/share/instagram';

import type { ShareActionComponentPropsWithOptions } from '../types';

import { useEducationModal } from './useEducationModal';

export const InstagramShare: React.FC<ShareActionComponentPropsWithOptions> = ({
	children,
	options,
	onAfterAction,
	onBeforeAction,
}) => {
	/**
	 * useEducationModal handles context detection internally:
	 * - returns [sharedOpenFn, null] when ShareEducationModalProvider is in the tree
	 * - returns [localOpenFn, <Modal>] otherwise (standalone fallback)
	 */
	const [openEducationModal, educationModal] = useEducationModal();
	const track = useTracking();
	const utilityContext = useUtilityContext();

	const onClick = useMemo(() => {
		return () => {
			void (async () => {
				await onBeforeAction?.();

				const { url, trackingData, trackingEventName } = options;

				const success = await shareInstagram(
					url,
					{
						trackingData,
						trackingEvents: {
							success: trackingEventName,
							intent: 'instagram_share_story_modal_view',
							continue: 'instagram_share_story_modal_continue',
							cancel: 'instagram_share_story_modal_close',
						},
						openEducationModal: () => openEducationModal(),
					},
					track,
					utilityContext.i18n,
				);

				await onAfterAction?.(success);
			})();
		};
	}, [onBeforeAction, onAfterAction, options, openEducationModal, track, utilityContext]);

	return (
		<>
			{React.cloneElement(children, {
				...children.props,
				onClick,
			})}
			{educationModal}
		</>
	);
};
